/**
 * Listing family registration layer (Phases 1–3).
 *
 * Single catalog for family identity, aliases, hide sets, attr keys, and
 * module pointers. Phase 3: sewing pilot hubs use registry as membership SSOT
 * (hard-coded sewing hub branches removed). Non-registered families keep
 * their hard-coded paths until migrated.
 *
 * Owner path locked D2: `src/lib/listing/listingFamilyRegistry.ts`
 */

import {
  APPLIANCE_INVERTER_TYPES,
  CANONICAL_AIR_CONDITIONER_SUBCATEGORY_ID,
  CANONICAL_MICROWAVE_SUBCATEGORY_ID,
  CANONICAL_WASHING_MACHINE_SUBCATEGORY_ID,
  LEGACY_INVERTER_AIR_CONDITIONER_SUBCATEGORY_ID,
  LEGACY_INVERTER_WASHING_MACHINE_SUBCATEGORY_ID,
  LEGACY_MICROWAVE_SUBCATEGORY_IDS,
  MICROWAVE_SMART_TYPES,
} from "@/lib/listing/applianceInverterListingSpec";
import {
  CANONICAL_FAN_SUBCATEGORY_ID,
  FAN_MOUNT_TYPES,
  FAN_POWER_SOURCES,
  LEGACY_FAN_SUBCATEGORY_IDS,
} from "@/lib/listing/fanListingSpec";
import {
  CANONICAL_FRIDGE_SUBCATEGORY_ID,
  FRIDGE_DOOR_TYPES,
  FRIDGE_SMART_TYPES,
  LEGACY_FRIDGE_SUBCATEGORY_IDS,
} from "@/lib/listing/fridgeListingSpec";
import {
  CANONICAL_GAS_COOKER_SUBCATEGORY_ID,
  GAS_COOKER_MOUNT_TYPES,
  LEGACY_GAS_COOKER_SUBCATEGORY_IDS,
} from "@/lib/listing/gasCookerListingSpec";
import {
  CANONICAL_KITCHEN_CABINET_SUBCATEGORY_ID,
  KITCHEN_CABINET_MOUNT_TYPES,
  LEGACY_KITCHEN_CABINET_SUBCATEGORY_IDS,
} from "@/lib/listing/kitchenCabinetListingSpec";
import {
  CANONICAL_PLAY_STATION_SUBCATEGORY_ID,
  LEGACY_PLAY_STATION_SUBCATEGORY_IDS,
  PLAY_STATION_EDITIONS,
  PLAY_STATION_GENERATIONS,
} from "@/lib/listing/playStationListingSpec";
import {
  CHAIR_MATERIALS,
  CHAIR_QUANTITY_MODES,
  LEGACY_L_SHAPED_SUBCATEGORY_IDS,
  SOFA_SHAPES,
} from "@/lib/listing/chairListingSpec";
import {
  DINING_CHAIR_COUNTS,
  DINING_TABLE_CHAIR_SUBCATEGORY_ID,
  DINING_TABLE_TOP_MATERIALS,
} from "@/lib/listing/diningTableChairListingSpec";
import {
  FURNITURE_MATERIALS,
  FURNITURE_SURFACE_SUBCATEGORY_IDS,
  LEGACY_WARDROBE_DRESSER_SUBCATEGORY_ID,
} from "@/lib/listing/furnitureSurfaceListingSpec";
import {
  OFFICE_TABLE_CHAIRS_SUBCATEGORY_ID,
  OFFICE_TABLE_CHAIR_COUNTS,
  TABLE_CHAIR_COUNTS,
  TABLE_CHAIR_SUBCATEGORY_ID,
} from "@/lib/listing/furnitureTableBundleListingSpec";
import {
  CANONICAL_SEWING_MACHINE_SUBCATEGORY_ID,
  LEGACY_SEWING_MACHINE_SUBCATEGORY_IDS,
  SEWING_MACHINE_TYPES,
} from "@/lib/listing/sewingMachineListingSpec";
import { WASHER_LOADER_TYPES } from "@/lib/listing/washingMachineListingSpec";

/** Stable family ids used by the registry catalog. */
export type ListingFamilyId =
  | "sewing_machine"
  | "play_station"
  | "fan"
  | "fridge"
  | "gas_cooker"
  | "microwave"
  | "washing_machine"
  | "ac"
  | "kitchen_cabinet"
  | "chair"
  | "sofa"
  | "dining_table_chair"
  | "table_chair"
  | "office_table_chairs"
  | "furniture_surface";

/** Plugin kind string — mirrors `ListingSpecPluginKind` for registered families. */
export type ListingFamilyPluginKind =
  | "sewing_machine"
  | "play_station"
  | "fan"
  | "fridge"
  | "gas_cooker"
  | "microwave"
  | "washing_machine"
  | "ac"
  | "kitchen_cabinet"
  | "chair"
  | "sofa"
  | "dining_table_chair"
  | "table_chair"
  | "office_table_chairs"
  | "furniture_surface";

export type ListingFamilyAttrDef = {
  key: string;
  label: string;
  required: boolean;
  enumValues?: readonly string[];
};

export type ListingFamilySheetHooks = {
  typeLabels: readonly string[];
  applyCanonicalLabelsExport: string;
  isSheetOrSioTypeExport: string;
};

export type ListingFamilyWaClassifyHooks = {
  displayNameBySubcategoryId: Readonly<Record<string, string>>;
  inferAttrsFromTitle: boolean;
};

export type ListingFamilyTriageHooks = {
  pluralSlugMap: Readonly<Record<string, string>>;
  titleRulesIntent: readonly string[];
};

export type ListingFamilyJijiHooks = {
  slugBySubcategoryId: Readonly<Record<string, string>>;
};

/**
 * Complete family registration contract (Phase 0 D3).
 * Module paths are import tokens for docs + completeness checks — not dynamic imports.
 */
export type ListingFamilyRegistration = {
  familyId: ListingFamilyId;
  pluginKind: ListingFamilyPluginKind;
  formStateKey: string;
  sectionLabel: string;

  canonicalSubcategoryId: string;
  parentCategoryId: string;
  legacyAliasIds: readonly string[];
  /**
   * Live peer subcategory ids that share this family WITHOUT collapsing to
   * canonical (indexed like aliases for membership; identity resolve).
   */
  memberSubcategoryIds?: readonly string[];
  pickerHideIds: readonly string[];
  deprecatedReplacements: Readonly<Record<string, readonly string[]>>;

  attrKeys: readonly string[];
  attrDefs: readonly ListingFamilyAttrDef[];

  listingSpecModule: string;
  specFieldsComponent: string;
  pricingCohortModule: string;
  /**
   * Callable sell surface (`listingFamilyRuntime.ts`). Path token only —
   * not a dynamic import. SpecFields components stay out of this file.
   */
  runtimeModule: string;

  sheet: ListingFamilySheetHooks;
  waClassify: ListingFamilyWaClassifyHooks;
  triage: ListingFamilyTriageHooks;
  jiji: ListingFamilyJijiHooks;

  hidesSizeLine: boolean;
  /** Hide category-level brand picker (family title owns brand/type). */
  hidesBrandLine: boolean;
  /** Hide generic model field (generation/type pills are enough). */
  hidesModelLine: boolean;
  usesStructuredTitle: boolean;
  includeInReferencePricingCompsNest: boolean;
  backfillScriptPath?: string;
};

/** Path token for the sell-socket runtime. Not a dynamic import. */
export const LISTING_FAMILY_RUNTIME_MODULE =
  "@/lib/listing/listingFamilyRuntime" as const;

const SEWING_MACHINE_FAMILY: ListingFamilyRegistration = {
  familyId: "sewing_machine",
  pluginKind: "sewing_machine",
  formStateKey: "sewingMachineSpec",
  sectionLabel: "Sewing machine details",

  canonicalSubcategoryId: CANONICAL_SEWING_MACHINE_SUBCATEGORY_ID,
  parentCategoryId: "fashion",
  legacyAliasIds: [...LEGACY_SEWING_MACHINE_SUBCATEGORY_IDS],
  pickerHideIds: [...LEGACY_SEWING_MACHINE_SUBCATEGORY_IDS],
  deprecatedReplacements: {
    "manual-sewing-machine": [CANONICAL_SEWING_MACHINE_SUBCATEGORY_ID],
    "electric-sewing-machine": [CANONICAL_SEWING_MACHINE_SUBCATEGORY_ID],
    "industrial-sewing-machine": [CANONICAL_SEWING_MACHINE_SUBCATEGORY_ID],
  },

  attrKeys: ["sewing_machine_type"],
  attrDefs: [
    {
      key: "sewing_machine_type",
      label: "Type",
      required: true,
      enumValues: SEWING_MACHINE_TYPES,
    },
  ],

  listingSpecModule: "@/lib/listing/sewingMachineListingSpec",
  specFieldsComponent: "@/components/listing/SewingMachineSpecFields",
  pricingCohortModule: "@/lib/pricing/sewingMachinePricingCohort",
  runtimeModule: LISTING_FAMILY_RUNTIME_MODULE,

  sheet: {
    typeLabels: [
      "Sewing Machines",
      "Manual Sewing Machines",
      "Electric Sewing Machines",
      "Industrial Sewing Machines",
    ],
    applyCanonicalLabelsExport: "applySewingMachineCanonicalLabels",
    isSheetOrSioTypeExport: "isSewingMachineSheetOrSioType",
  },
  waClassify: {
    displayNameBySubcategoryId: {
      "sewing-machine": "Sewing Machines",
      "manual-sewing-machine": "Manual Sewing Machines",
      "electric-sewing-machine": "Electric Sewing Machines",
      "industrial-sewing-machine": "Industrial Sewing Machines",
    },
    inferAttrsFromTitle: true,
  },
  triage: {
    pluralSlugMap: {
      "industrial-sewing-machines": CANONICAL_SEWING_MACHINE_SUBCATEGORY_ID,
      "electric-sewing-machines": CANONICAL_SEWING_MACHINE_SUBCATEGORY_ID,
      "manual-sewing-machines": CANONICAL_SEWING_MACHINE_SUBCATEGORY_ID,
    },
    titleRulesIntent: [
      "industrial sewing → sewing-machine",
      "sewing machine → sewing-machine",
    ],
  },
  jiji: {
    slugBySubcategoryId: {
      "sewing-machine": "home-furniture-appliances",
      "manual-sewing-machine": "home-furniture-appliances",
      "electric-sewing-machine": "home-furniture-appliances",
      "industrial-sewing-machine": "home-furniture-appliances",
    },
  },

  hidesSizeLine: true,
  hidesBrandLine: false,
  hidesModelLine: false,
  usesStructuredTitle: true,
  includeInReferencePricingCompsNest: true,
  backfillScriptPath: "scripts/backfill/backfill-sewing-machine-unified.ts",
};

const PLAY_STATION_FAMILY: ListingFamilyRegistration = {
  familyId: "play_station",
  pluginKind: "play_station",
  formStateKey: "playStationSpec",
  sectionLabel: "Play Station details",

  canonicalSubcategoryId: CANONICAL_PLAY_STATION_SUBCATEGORY_ID,
  parentCategoryId: "gaming",
  legacyAliasIds: [...LEGACY_PLAY_STATION_SUBCATEGORY_IDS],
  pickerHideIds: [...LEGACY_PLAY_STATION_SUBCATEGORY_IDS],
  deprecatedReplacements: {
    "gaming-consoles": [CANONICAL_PLAY_STATION_SUBCATEGORY_ID],
    "gaming-console": [CANONICAL_PLAY_STATION_SUBCATEGORY_ID],
    "play-stations": [CANONICAL_PLAY_STATION_SUBCATEGORY_ID],
  },

  attrKeys: [
    "play_station_generation",
    "play_station_edition",
    "play_station_pad_count",
    "play_station_games_count",
  ],
  attrDefs: [
    {
      key: "play_station_generation",
      label: "Generation",
      required: true,
      enumValues: PLAY_STATION_GENERATIONS,
    },
    {
      key: "play_station_edition",
      label: "Edition",
      required: false,
      enumValues: PLAY_STATION_EDITIONS,
    },
    {
      key: "play_station_pad_count",
      label: "Number of pads",
      required: false,
    },
    {
      key: "play_station_games_count",
      label: "Available games",
      required: false,
    },
  ],

  listingSpecModule: "@/lib/listing/playStationListingSpec",
  specFieldsComponent: "@/components/listing/PlayStationSpecFields",
  pricingCohortModule: "@/lib/pricing/playStationPricingCohort",
  runtimeModule: LISTING_FAMILY_RUNTIME_MODULE,

  sheet: {
    typeLabels: ["Play Stations", "Play Station", "Playstation", "PS4", "PS5"],
    applyCanonicalLabelsExport: "applyPlayStationCanonicalLabels",
    isSheetOrSioTypeExport: "isPlayStationSheetOrSioType",
  },
  waClassify: {
    displayNameBySubcategoryId: {
      "play-station": "Play Stations",
      "gaming-consoles": "Play Stations",
      "gaming-console": "Play Stations",
      "play-stations": "Play Stations",
    },
    inferAttrsFromTitle: true,
  },
  triage: {
    pluralSlugMap: {
      "play-stations": CANONICAL_PLAY_STATION_SUBCATEGORY_ID,
      "gaming-consoles": CANONICAL_PLAY_STATION_SUBCATEGORY_ID,
    },
    titleRulesIntent: [
      "playstation / ps4 / ps5 → play-station",
      "gaming console (legacy) → play-station",
    ],
  },
  jiji: {
    slugBySubcategoryId: {
      "play-station": "video-games-and-consoles",
      "gaming-consoles": "video-games-and-consoles",
      "gaming-console": "video-games-and-consoles",
      "play-stations": "video-games-and-consoles",
    },
  },

  hidesSizeLine: true,
  hidesBrandLine: true,
  hidesModelLine: true,
  usesStructuredTitle: true,
  includeInReferencePricingCompsNest: true,
};

const FAN_FAMILY: ListingFamilyRegistration = {
  familyId: "fan",
  pluginKind: "fan",
  formStateKey: "fanSpec",
  sectionLabel: "Fan details",

  canonicalSubcategoryId: CANONICAL_FAN_SUBCATEGORY_ID,
  parentCategoryId: "electronics",
  legacyAliasIds: [...LEGACY_FAN_SUBCATEGORY_IDS],
  pickerHideIds: [...LEGACY_FAN_SUBCATEGORY_IDS],
  deprecatedReplacements: {
    "standing-fan": [CANONICAL_FAN_SUBCATEGORY_ID],
    "wall-fan": [CANONICAL_FAN_SUBCATEGORY_ID],
    "ceiling-fan": [CANONICAL_FAN_SUBCATEGORY_ID],
    "rechargeable-fans": [CANONICAL_FAN_SUBCATEGORY_ID],
  },

  attrKeys: ["fan_mount_type", "fan_power_source", "blade_size_inches"],
  attrDefs: [
    {
      key: "fan_mount_type",
      label: "Mount type",
      required: true,
      enumValues: FAN_MOUNT_TYPES,
    },
    {
      key: "fan_power_source",
      label: "Power source",
      required: true,
      enumValues: FAN_POWER_SOURCES,
    },
    {
      key: "blade_size_inches",
      label: "Blade size (inches)",
      required: false,
    },
  ],

  listingSpecModule: "@/lib/listing/fanListingSpec",
  specFieldsComponent: "@/components/listing/FanSpecFields",
  pricingCohortModule: "@/lib/pricing/fanPricingCohort",
  runtimeModule: LISTING_FAMILY_RUNTIME_MODULE,

  sheet: {
    typeLabels: [
      "Fans",
      "Standing Fans",
      "Wall Fans",
      "Ceiling Fans",
      "Rechargeable Fans",
    ],
    applyCanonicalLabelsExport: "applyFanCanonicalLabels",
    isSheetOrSioTypeExport: "isFanSheetOrSioType",
  },
  waClassify: {
    displayNameBySubcategoryId: {
      fan: "Fans",
      "standing-fan": "Standing Fans",
      "wall-fan": "Wall Fans",
      "ceiling-fan": "Ceiling Fans",
      "rechargeable-fans": "Rechargeable Fans",
    },
    inferAttrsFromTitle: true,
  },
  triage: {
    pluralSlugMap: {
      "standing-fans": CANONICAL_FAN_SUBCATEGORY_ID,
      "wall-fans": CANONICAL_FAN_SUBCATEGORY_ID,
      "ceiling-fans": CANONICAL_FAN_SUBCATEGORY_ID,
      "rechargeable-fans": CANONICAL_FAN_SUBCATEGORY_ID,
    },
    titleRulesIntent: ["standing / wall / ceiling / rechargeable fan → fan"],
  },
  jiji: {
    slugBySubcategoryId: {
      fan: "home-appliances",
      "standing-fan": "home-appliances",
      "wall-fan": "home-appliances",
      "ceiling-fan": "home-appliances",
      "rechargeable-fans": "home-appliances",
    },
  },

  hidesSizeLine: true,
  hidesBrandLine: false,
  hidesModelLine: false,
  usesStructuredTitle: true,
  includeInReferencePricingCompsNest: true,
};

const FRIDGE_FAMILY: ListingFamilyRegistration = {
  familyId: "fridge",
  pluginKind: "fridge",
  formStateKey: "fridgeSpec",
  sectionLabel: "Fridge details",

  canonicalSubcategoryId: CANONICAL_FRIDGE_SUBCATEGORY_ID,
  parentCategoryId: "kitchen",
  legacyAliasIds: [...LEGACY_FRIDGE_SUBCATEGORY_IDS],
  pickerHideIds: [...LEGACY_FRIDGE_SUBCATEGORY_IDS],
  deprecatedReplacements: {
    "standing-fridge": [CANONICAL_FRIDGE_SUBCATEGORY_ID],
    "mini-fridge": [CANONICAL_FRIDGE_SUBCATEGORY_ID],
    "inverter-fridge": [CANONICAL_FRIDGE_SUBCATEGORY_ID],
    "side-by-side-fridge": [CANONICAL_FRIDGE_SUBCATEGORY_ID],
  },

  attrKeys: [
    "fridge_door_type",
    "capacity_liters",
    "appliance_inverter_type",
    "fridge_smart_type",
  ],
  attrDefs: [
    {
      key: "fridge_door_type",
      label: "Door type",
      required: true,
      enumValues: FRIDGE_DOOR_TYPES,
    },
    {
      key: "capacity_liters",
      label: "Capacity (liters)",
      required: false,
    },
    {
      key: "appliance_inverter_type",
      label: "Inverter",
      required: true,
      enumValues: APPLIANCE_INVERTER_TYPES,
    },
    {
      key: "fridge_smart_type",
      label: "Smart",
      required: true,
      enumValues: FRIDGE_SMART_TYPES,
    },
  ],

  listingSpecModule: "@/lib/listing/fridgeListingSpec",
  specFieldsComponent: "@/components/listing/FridgeSpecFields",
  pricingCohortModule: "@/lib/pricing/fridgePricingCohort",
  runtimeModule: LISTING_FAMILY_RUNTIME_MODULE,

  sheet: {
    typeLabels: [
      "Fridges",
      "Standing Fridges",
      "Mini Fridges",
      "Inverter Fridges",
      "Side-by-Side Fridges",
    ],
    applyCanonicalLabelsExport: "applyFridgeCanonicalLabels",
    isSheetOrSioTypeExport: "isFridgeSheetOrSioType",
  },
  waClassify: {
    displayNameBySubcategoryId: {
      fridge: "Fridges",
      "standing-fridge": "Standing Fridges",
      "mini-fridge": "Mini Fridges",
      "inverter-fridge": "Inverter Fridges",
      "side-by-side-fridge": "Side-by-Side Fridges",
    },
    inferAttrsFromTitle: true,
  },
  triage: {
    pluralSlugMap: {
      "standing-fridges": CANONICAL_FRIDGE_SUBCATEGORY_ID,
      fridges: CANONICAL_FRIDGE_SUBCATEGORY_ID,
    },
    titleRulesIntent: ["fridge / standing fridge → fridge"],
  },
  jiji: {
    slugBySubcategoryId: {
      fridge: "kitchen-appliances",
      "standing-fridge": "kitchen-appliances",
      "mini-fridge": "kitchen-appliances",
      "inverter-fridge": "kitchen-appliances",
      "side-by-side-fridge": "kitchen-appliances",
    },
  },

  hidesSizeLine: true,
  hidesBrandLine: false,
  hidesModelLine: false,
  usesStructuredTitle: true,
  includeInReferencePricingCompsNest: true,
};

const GAS_COOKER_FAMILY: ListingFamilyRegistration = {
  familyId: "gas_cooker",
  pluginKind: "gas_cooker",
  formStateKey: "gasCookerSpec",
  sectionLabel: "Gas cooker details",

  canonicalSubcategoryId: CANONICAL_GAS_COOKER_SUBCATEGORY_ID,
  parentCategoryId: "kitchen",
  legacyAliasIds: [...LEGACY_GAS_COOKER_SUBCATEGORY_IDS],
  pickerHideIds: [...LEGACY_GAS_COOKER_SUBCATEGORY_IDS],
  deprecatedReplacements: {
    "standing-gas-cooker": [CANONICAL_GAS_COOKER_SUBCATEGORY_ID],
    "table-top-gas-cooker": [CANONICAL_GAS_COOKER_SUBCATEGORY_ID],
    "standing-gas-cooker-cylinder": [CANONICAL_GAS_COOKER_SUBCATEGORY_ID],
    "table-top-gas-cooker-cylinder": [CANONICAL_GAS_COOKER_SUBCATEGORY_ID],
  },

  attrKeys: ["gas_cooker_mount_type", "gas_cylinder_included", "burner_count"],
  attrDefs: [
    {
      key: "gas_cooker_mount_type",
      label: "Mount type",
      required: true,
      enumValues: GAS_COOKER_MOUNT_TYPES,
    },
    {
      key: "gas_cylinder_included",
      label: "Cylinder included",
      required: true,
    },
    {
      key: "burner_count",
      label: "Burner count",
      required: false,
    },
  ],

  listingSpecModule: "@/lib/listing/gasCookerListingSpec",
  specFieldsComponent: "@/components/listing/GasCookerSpecFields",
  pricingCohortModule: "@/lib/pricing/gasCookerPricingCohort",
  runtimeModule: LISTING_FAMILY_RUNTIME_MODULE,

  sheet: {
    typeLabels: [
      "Gas Cookers",
      "Standing Gas Cookers",
      "Table-top Gas Cookers",
      "Standing Gas Cookers & Cylinders",
      "Table-top Gas Cookers & Cylinders",
    ],
    applyCanonicalLabelsExport: "applyGasCookerCanonicalLabels",
    isSheetOrSioTypeExport: "isGasCookerSheetOrSioType",
  },
  waClassify: {
    displayNameBySubcategoryId: {
      "gas-cooker": "Gas Cookers",
      "standing-gas-cooker": "Standing Gas Cookers",
      "table-top-gas-cooker": "Table-top Gas Cookers",
      "standing-gas-cooker-cylinder": "Standing Gas Cookers & Cylinders",
      "table-top-gas-cooker-cylinder": "Table-top Gas Cookers & Cylinders",
    },
    inferAttrsFromTitle: true,
  },
  triage: {
    pluralSlugMap: {
      "standing-gas-cookers": CANONICAL_GAS_COOKER_SUBCATEGORY_ID,
      "table-top-gas-cookers": CANONICAL_GAS_COOKER_SUBCATEGORY_ID,
    },
    titleRulesIntent: ["gas cooker / standing gas cooker → gas-cooker"],
  },
  jiji: {
    slugBySubcategoryId: {
      "gas-cooker": "kitchen-appliances",
      "standing-gas-cooker": "kitchen-appliances",
      "table-top-gas-cooker": "kitchen-appliances",
      "standing-gas-cooker-cylinder": "kitchen-appliances",
      "table-top-gas-cooker-cylinder": "kitchen-appliances",
    },
  },

  hidesSizeLine: true,
  hidesBrandLine: false,
  hidesModelLine: false,
  usesStructuredTitle: true,
  includeInReferencePricingCompsNest: true,
};

const MICROWAVE_FAMILY: ListingFamilyRegistration = {
  familyId: "microwave",
  pluginKind: "microwave",
  formStateKey: "microwaveSpec",
  sectionLabel: "Microwave details",

  canonicalSubcategoryId: CANONICAL_MICROWAVE_SUBCATEGORY_ID,
  parentCategoryId: "kitchen",
  legacyAliasIds: [...LEGACY_MICROWAVE_SUBCATEGORY_IDS],
  pickerHideIds: [...LEGACY_MICROWAVE_SUBCATEGORY_IDS],
  deprecatedReplacements: {
    "inverter-microwave": [CANONICAL_MICROWAVE_SUBCATEGORY_ID],
    "non-inverter-microwave": [CANONICAL_MICROWAVE_SUBCATEGORY_ID],
    "smart-microwave": [CANONICAL_MICROWAVE_SUBCATEGORY_ID],
    "non-smart-microwave": [CANONICAL_MICROWAVE_SUBCATEGORY_ID],
  },

  attrKeys: ["appliance_inverter_type", "microwave_smart_type"],
  attrDefs: [
    {
      key: "appliance_inverter_type",
      label: "Inverter",
      required: true,
      enumValues: APPLIANCE_INVERTER_TYPES,
    },
    {
      key: "microwave_smart_type",
      label: "Smart",
      required: true,
      enumValues: MICROWAVE_SMART_TYPES,
    },
  ],

  listingSpecModule: "@/lib/listing/microwaveListingSpec",
  specFieldsComponent: "@/components/listing/MicrowaveSpecFields",
  pricingCohortModule: "@/lib/pricing/applianceInverterPricingCohort",
  runtimeModule: LISTING_FAMILY_RUNTIME_MODULE,

  sheet: {
    typeLabels: [
      "Microwaves",
      "Inverter Microwaves",
      "Non-Inverter Microwaves",
      "Smart Microwaves",
      "Non-Smart Microwaves",
    ],
    applyCanonicalLabelsExport: "microwaveLabelsFromSheetOrLegacy",
    isSheetOrSioTypeExport: "isMicrowaveSheetOrSioType",
  },
  waClassify: {
    displayNameBySubcategoryId: {
      microwave: "Microwaves",
      "inverter-microwave": "Inverter Microwaves",
      "non-inverter-microwave": "Non-Inverter Microwaves",
      "smart-microwave": "Smart Microwaves",
      "non-smart-microwave": "Non-Smart Microwaves",
    },
    inferAttrsFromTitle: true,
  },
  triage: {
    pluralSlugMap: {
      microwaves: CANONICAL_MICROWAVE_SUBCATEGORY_ID,
      "smart-microwaves": CANONICAL_MICROWAVE_SUBCATEGORY_ID,
      "non-smart-microwaves": CANONICAL_MICROWAVE_SUBCATEGORY_ID,
      "inverter-microwaves": CANONICAL_MICROWAVE_SUBCATEGORY_ID,
      "non-inverter-microwaves": CANONICAL_MICROWAVE_SUBCATEGORY_ID,
    },
    titleRulesIntent: ["microwave / inverter microwave → microwave"],
  },
  jiji: {
    slugBySubcategoryId: {
      microwave: "kitchen-appliances",
      "inverter-microwave": "kitchen-appliances",
      "non-inverter-microwave": "kitchen-appliances",
      "smart-microwave": "kitchen-appliances",
      "non-smart-microwave": "kitchen-appliances",
    },
  },

  hidesSizeLine: true,
  hidesBrandLine: false,
  hidesModelLine: false,
  usesStructuredTitle: true,
  includeInReferencePricingCompsNest: true,
};

const WASHING_MACHINE_FAMILY: ListingFamilyRegistration = {
  familyId: "washing_machine",
  pluginKind: "washing_machine",
  formStateKey: "washingMachineSpec",
  sectionLabel: "Washing machine details",

  canonicalSubcategoryId: CANONICAL_WASHING_MACHINE_SUBCATEGORY_ID,
  parentCategoryId: "electronics",
  legacyAliasIds: [LEGACY_INVERTER_WASHING_MACHINE_SUBCATEGORY_ID],
  pickerHideIds: [LEGACY_INVERTER_WASHING_MACHINE_SUBCATEGORY_ID],
  deprecatedReplacements: {
    "inverter-washing-machine": [CANONICAL_WASHING_MACHINE_SUBCATEGORY_ID],
  },

  attrKeys: [
    "load_capacity_kg",
    "appliance_inverter_type",
    "washer_loader_type",
  ],
  attrDefs: [
    {
      key: "load_capacity_kg",
      label: "Load capacity (kg)",
      required: true,
    },
    {
      key: "appliance_inverter_type",
      label: "Inverter",
      required: true,
      enumValues: APPLIANCE_INVERTER_TYPES,
    },
    {
      key: "washer_loader_type",
      label: "Loader type",
      required: true,
      enumValues: WASHER_LOADER_TYPES,
    },
  ],

  listingSpecModule: "@/lib/listing/washingMachineListingSpec",
  specFieldsComponent: "@/components/listing/WashingMachineSpecFields",
  pricingCohortModule: "@/lib/pricing/washingMachinePricingCohort",
  runtimeModule: LISTING_FAMILY_RUNTIME_MODULE,

  sheet: {
    typeLabels: ["Washing Machines", "Inverter Washing Machines"],
    applyCanonicalLabelsExport: "applyWashingMachineCanonicalLabels",
    isSheetOrSioTypeExport: "isWashingMachineSheetOrSioType",
  },
  waClassify: {
    displayNameBySubcategoryId: {
      "washing-machine": "Washing Machines",
      "inverter-washing-machine": "Inverter Washing Machines",
    },
    inferAttrsFromTitle: true,
  },
  triage: {
    pluralSlugMap: {
      "washing-machines": CANONICAL_WASHING_MACHINE_SUBCATEGORY_ID,
      "inverter-washing-machines": CANONICAL_WASHING_MACHINE_SUBCATEGORY_ID,
    },
    titleRulesIntent: ["washing machine → washing-machine"],
  },
  jiji: {
    slugBySubcategoryId: {
      "washing-machine": "home-appliances",
      "inverter-washing-machine": "home-appliances",
    },
  },

  hidesSizeLine: true,
  hidesBrandLine: false,
  hidesModelLine: false,
  usesStructuredTitle: true,
  includeInReferencePricingCompsNest: true,
};

const AC_FAMILY: ListingFamilyRegistration = {
  familyId: "ac",
  pluginKind: "ac",
  formStateKey: "acSpec",
  sectionLabel: "Air conditioner details",

  canonicalSubcategoryId: CANONICAL_AIR_CONDITIONER_SUBCATEGORY_ID,
  parentCategoryId: "electronics",
  legacyAliasIds: [LEGACY_INVERTER_AIR_CONDITIONER_SUBCATEGORY_ID],
  pickerHideIds: [LEGACY_INVERTER_AIR_CONDITIONER_SUBCATEGORY_ID],
  deprecatedReplacements: {
    "inverter-air-conditioner": [CANONICAL_AIR_CONDITIONER_SUBCATEGORY_ID],
  },

  attrKeys: [
    "horse_power",
    "appliance_inverter_type",
    "ac_remote_included",
    "ac_installation_kit_included",
  ],
  attrDefs: [
    {
      key: "horse_power",
      label: "Horse power",
      required: true,
    },
    {
      key: "appliance_inverter_type",
      label: "Inverter",
      required: true,
      enumValues: APPLIANCE_INVERTER_TYPES,
    },
    {
      key: "ac_remote_included",
      label: "Remote included",
      required: false,
    },
    {
      key: "ac_installation_kit_included",
      label: "Installation kit included",
      required: false,
    },
  ],

  listingSpecModule: "@/lib/listing/acListingSpec",
  specFieldsComponent: "@/components/listing/AcSpecFields",
  pricingCohortModule: "@/lib/pricing/applianceInverterPricingCohort",
  runtimeModule: LISTING_FAMILY_RUNTIME_MODULE,

  sheet: {
    typeLabels: ["Air Conditioners", "Inverter Air Conditioners"],
    applyCanonicalLabelsExport: "applyAcCanonicalLabels",
    isSheetOrSioTypeExport: "isAirConditionerSheetOrSioType",
  },
  waClassify: {
    displayNameBySubcategoryId: {
      "air-conditioner": "Air Conditioners",
      "inverter-air-conditioner": "Inverter Air Conditioners",
    },
    inferAttrsFromTitle: true,
  },
  triage: {
    pluralSlugMap: {
      "air-conditioners": CANONICAL_AIR_CONDITIONER_SUBCATEGORY_ID,
      "inverter-air-conditioners": CANONICAL_AIR_CONDITIONER_SUBCATEGORY_ID,
    },
    titleRulesIntent: ["air conditioner / AC → air-conditioner"],
  },
  jiji: {
    slugBySubcategoryId: {
      "air-conditioner": "home-appliances",
      "inverter-air-conditioner": "home-appliances",
    },
  },

  hidesSizeLine: true,
  hidesBrandLine: false,
  hidesModelLine: false,
  usesStructuredTitle: true,
  includeInReferencePricingCompsNest: true,
};

const KITCHEN_CABINET_FAMILY: ListingFamilyRegistration = {
  familyId: "kitchen_cabinet",
  pluginKind: "kitchen_cabinet",
  formStateKey: "kitchenCabinetSpec",
  sectionLabel: "Kitchen cabinet details",

  canonicalSubcategoryId: CANONICAL_KITCHEN_CABINET_SUBCATEGORY_ID,
  parentCategoryId: "furniture",
  legacyAliasIds: [...LEGACY_KITCHEN_CABINET_SUBCATEGORY_IDS],
  pickerHideIds: [...LEGACY_KITCHEN_CABINET_SUBCATEGORY_IDS],
  deprecatedReplacements: {
    "hanging-kitchen-cabinet": [CANONICAL_KITCHEN_CABINET_SUBCATEGORY_ID],
  },

  attrKeys: ["kitchen_cabinet_mount_type"],
  attrDefs: [
    {
      key: "kitchen_cabinet_mount_type",
      label: "Mount type",
      required: true,
      enumValues: KITCHEN_CABINET_MOUNT_TYPES,
    },
  ],

  listingSpecModule: "@/lib/listing/kitchenCabinetListingSpec",
  specFieldsComponent: "@/components/listing/KitchenCabinetSpecFields",
  pricingCohortModule: "@/lib/pricing/kitchenCabinetPricingCohort",
  runtimeModule: LISTING_FAMILY_RUNTIME_MODULE,

  sheet: {
    typeLabels: ["Kitchen Cabinets", "Hanging Kitchen Cabinets"],
    applyCanonicalLabelsExport: "applyKitchenCabinetCanonicalLabels",
    isSheetOrSioTypeExport: "isKitchenCabinetSheetOrSioType",
  },
  waClassify: {
    displayNameBySubcategoryId: {
      "kitchen-cabinet": "Kitchen Cabinets",
      "hanging-kitchen-cabinet": "Hanging Kitchen Cabinets",
    },
    inferAttrsFromTitle: true,
  },
  triage: {
    pluralSlugMap: {
      "kitchen-cabinets": CANONICAL_KITCHEN_CABINET_SUBCATEGORY_ID,
      "hanging-kitchen-cabinets": CANONICAL_KITCHEN_CABINET_SUBCATEGORY_ID,
    },
    titleRulesIntent: ["kitchen cabinet / hanging → kitchen-cabinet"],
  },
  jiji: {
    slugBySubcategoryId: {
      "kitchen-cabinet": "furniture",
      "hanging-kitchen-cabinet": "furniture",
    },
  },

  hidesSizeLine: true,
  hidesBrandLine: false,
  hidesModelLine: false,
  usesStructuredTitle: true,
  includeInReferencePricingCompsNest: true,
};

const CHAIR_FAMILY: ListingFamilyRegistration = {
  familyId: "chair",
  pluginKind: "chair",
  formStateKey: "chairSpec",
  sectionLabel: "Chair details",

  canonicalSubcategoryId: "chair",
  parentCategoryId: "furniture",
  legacyAliasIds: [],
  pickerHideIds: [],
  deprecatedReplacements: {},

  attrKeys: ["chair_quantity_mode", "chair_seat_capacities", "chair_material"],
  attrDefs: [
    {
      key: "chair_quantity_mode",
      label: "Quantity",
      required: true,
      enumValues: CHAIR_QUANTITY_MODES,
    },
    {
      key: "chair_seat_capacities",
      label: "Seating capacity",
      required: true,
    },
    {
      key: "chair_material",
      label: "Material",
      required: true,
      enumValues: CHAIR_MATERIALS,
    },
  ],

  listingSpecModule: "@/lib/listing/chairListingSpec",
  specFieldsComponent: "@/components/listing/ChairSpecFields",
  pricingCohortModule: "@/lib/pricing/furnitureSellSocketPricingCohort",
  runtimeModule: LISTING_FAMILY_RUNTIME_MODULE,

  sheet: {
    typeLabels: ["Chairs"],
    // Closest sheet helpers live on sofa/L-shaped; chair rows use category type labels.
    applyCanonicalLabelsExport: "applyLShapedSofaCanonicalLabels",
    isSheetOrSioTypeExport: "isLShapedSofaSheetOrSioType",
  },
  waClassify: {
    displayNameBySubcategoryId: {
      chair: "Chairs",
    },
    inferAttrsFromTitle: true,
  },
  triage: {
    pluralSlugMap: {
      chairs: "chair",
    },
    titleRulesIntent: ["chair → chair"],
  },
  jiji: {
    slugBySubcategoryId: {
      chair: "furniture",
    },
  },

  hidesSizeLine: true,
  hidesBrandLine: false,
  hidesModelLine: false,
  usesStructuredTitle: true,
  includeInReferencePricingCompsNest: true,
};

const SOFA_FAMILY: ListingFamilyRegistration = {
  familyId: "sofa",
  pluginKind: "sofa",
  formStateKey: "chairSpec",
  sectionLabel: "Sofa details",

  canonicalSubcategoryId: "sofa-couche",
  parentCategoryId: "furniture",
  legacyAliasIds: [...LEGACY_L_SHAPED_SUBCATEGORY_IDS],
  pickerHideIds: [...LEGACY_L_SHAPED_SUBCATEGORY_IDS],
  deprecatedReplacements: {
    "l-shaped-sofa": ["sofa-couche"],
    "l-shaped-chair": ["sofa-couche"],
  },

  attrKeys: [
    "sofa_shape",
    "chair_quantity_mode",
    "chair_seat_capacities",
    "chair_material",
  ],
  attrDefs: [
    {
      key: "sofa_shape",
      label: "Shape",
      required: true,
      enumValues: SOFA_SHAPES,
    },
    {
      key: "chair_quantity_mode",
      label: "Quantity",
      required: false,
      enumValues: CHAIR_QUANTITY_MODES,
    },
    {
      key: "chair_seat_capacities",
      label: "Seating capacity",
      required: false,
    },
    {
      key: "chair_material",
      label: "Material",
      required: true,
      enumValues: CHAIR_MATERIALS,
    },
  ],

  listingSpecModule: "@/lib/listing/chairListingSpec",
  specFieldsComponent: "@/components/listing/ChairSpecFields",
  pricingCohortModule: "@/lib/pricing/lShapedPricingCohort",
  runtimeModule: LISTING_FAMILY_RUNTIME_MODULE,

  sheet: {
    typeLabels: ["Sofas", "L-shaped Sofas", "L-shaped Chairs"],
    applyCanonicalLabelsExport: "applyLShapedSofaCanonicalLabels",
    isSheetOrSioTypeExport: "isLShapedSofaSheetOrSioType",
  },
  waClassify: {
    displayNameBySubcategoryId: {
      "sofa-couche": "Sofas",
      "l-shaped-sofa": "L-shaped Sofas",
      "l-shaped-chair": "L-shaped Chairs",
    },
    inferAttrsFromTitle: true,
  },
  triage: {
    pluralSlugMap: {
      sofas: "sofa-couche",
      couches: "sofa-couche",
      "l-shaped-sofas": "sofa-couche",
    },
    titleRulesIntent: [
      "sofa / couche → sofa-couche",
      "l-shaped sofa/chair → sofa-couche",
    ],
  },
  jiji: {
    slugBySubcategoryId: {
      "sofa-couche": "furniture",
      "l-shaped-sofa": "furniture",
      "l-shaped-chair": "furniture",
    },
  },

  hidesSizeLine: true,
  hidesBrandLine: false,
  hidesModelLine: false,
  usesStructuredTitle: true,
  includeInReferencePricingCompsNest: true,
};

const DINING_TABLE_CHAIR_FAMILY: ListingFamilyRegistration = {
  familyId: "dining_table_chair",
  pluginKind: "dining_table_chair",
  formStateKey: "diningTableChairSpec",
  sectionLabel: "Dining table & chair details",

  canonicalSubcategoryId: DINING_TABLE_CHAIR_SUBCATEGORY_ID,
  parentCategoryId: "furniture",
  legacyAliasIds: [],
  pickerHideIds: [],
  deprecatedReplacements: {},

  attrKeys: [
    "dining_chair_count",
    "dining_table_top_material",
    "chair_material",
  ],
  attrDefs: [
    {
      key: "dining_chair_count",
      label: "Chair count",
      required: true,
      enumValues: DINING_CHAIR_COUNTS.map(String),
    },
    {
      key: "dining_table_top_material",
      label: "Table top material",
      required: true,
      enumValues: DINING_TABLE_TOP_MATERIALS,
    },
    {
      key: "chair_material",
      label: "Chair material",
      required: true,
      enumValues: CHAIR_MATERIALS,
    },
  ],

  listingSpecModule: "@/lib/listing/diningTableChairListingSpec",
  specFieldsComponent: "@/components/listing/DiningTableChairSpecFields",
  pricingCohortModule: "@/lib/pricing/furnitureSellSocketPricingCohort",
  runtimeModule: LISTING_FAMILY_RUNTIME_MODULE,

  sheet: {
    typeLabels: ["Dining Table & Chairs"],
    // Title-split helper is module-local; pair check is the public export.
    applyCanonicalLabelsExport: "isDiningTableChairPair",
    isSheetOrSioTypeExport: "isDiningTableChairPair",
  },
  waClassify: {
    displayNameBySubcategoryId: {
      "dining-table-chair": "Dining Table & Chairs",
    },
    inferAttrsFromTitle: true,
  },
  triage: {
    pluralSlugMap: {
      "dining-table-chairs": DINING_TABLE_CHAIR_SUBCATEGORY_ID,
    },
    titleRulesIntent: ["dining table chair → dining-table-chair"],
  },
  jiji: {
    slugBySubcategoryId: {
      "dining-table-chair": "furniture",
    },
  },

  hidesSizeLine: true,
  hidesBrandLine: false,
  hidesModelLine: false,
  usesStructuredTitle: true,
  includeInReferencePricingCompsNest: true,
};

const TABLE_CHAIR_FAMILY: ListingFamilyRegistration = {
  familyId: "table_chair",
  pluginKind: "table_chair",
  formStateKey: "furnitureTableBundleSpec",
  sectionLabel: "Table & chair details",

  canonicalSubcategoryId: TABLE_CHAIR_SUBCATEGORY_ID,
  parentCategoryId: "furniture",
  legacyAliasIds: [],
  pickerHideIds: [],
  deprecatedReplacements: {},

  attrKeys: ["chair_count", "table_top_material", "chair_material"],
  attrDefs: [
    {
      key: "chair_count",
      label: "Chair count",
      required: true,
      enumValues: TABLE_CHAIR_COUNTS.map(String),
    },
    {
      key: "table_top_material",
      label: "Table top material",
      required: true,
      enumValues: DINING_TABLE_TOP_MATERIALS,
    },
    {
      key: "chair_material",
      label: "Chair material",
      required: true,
      enumValues: CHAIR_MATERIALS,
    },
  ],

  listingSpecModule: "@/lib/listing/furnitureTableBundleListingSpec",
  specFieldsComponent: "@/components/listing/TableChairBundleSpecFields",
  pricingCohortModule: "@/lib/pricing/furnitureSellSocketPricingCohort",
  runtimeModule: LISTING_FAMILY_RUNTIME_MODULE,

  sheet: {
    typeLabels: ["Table & Chairs"],
    // Shared dining pair/title helpers are the closest public sheet exports.
    applyCanonicalLabelsExport: "isDiningTableChairPair",
    isSheetOrSioTypeExport: "isDiningTableChairPair",
  },
  waClassify: {
    displayNameBySubcategoryId: {
      "table-chair": "Table & Chairs",
    },
    inferAttrsFromTitle: true,
  },
  triage: {
    pluralSlugMap: {
      "table-chairs": TABLE_CHAIR_SUBCATEGORY_ID,
    },
    titleRulesIntent: ["table chair → table-chair"],
  },
  jiji: {
    slugBySubcategoryId: {
      "table-chair": "furniture",
    },
  },

  hidesSizeLine: true,
  hidesBrandLine: false,
  hidesModelLine: false,
  usesStructuredTitle: true,
  includeInReferencePricingCompsNest: true,
};

const OFFICE_TABLE_CHAIRS_FAMILY: ListingFamilyRegistration = {
  familyId: "office_table_chairs",
  pluginKind: "office_table_chairs",
  formStateKey: "furnitureTableBundleSpec",
  sectionLabel: "Office desk & chair details",

  canonicalSubcategoryId: OFFICE_TABLE_CHAIRS_SUBCATEGORY_ID,
  parentCategoryId: "furniture",
  legacyAliasIds: [],
  pickerHideIds: [],
  deprecatedReplacements: {},

  attrKeys: ["chair_count", "chair_material", "table_top_material"],
  attrDefs: [
    {
      key: "chair_count",
      label: "Chair count",
      required: true,
      enumValues: OFFICE_TABLE_CHAIR_COUNTS.map(String),
    },
    {
      key: "chair_material",
      label: "Chair material",
      required: true,
      enumValues: CHAIR_MATERIALS,
    },
    {
      key: "table_top_material",
      label: "Desk top material",
      required: false,
      enumValues: DINING_TABLE_TOP_MATERIALS,
    },
  ],

  listingSpecModule: "@/lib/listing/furnitureTableBundleListingSpec",
  specFieldsComponent: "@/components/listing/OfficeTableChairsBundleSpecFields",
  pricingCohortModule: "@/lib/pricing/furnitureSellSocketPricingCohort",
  runtimeModule: LISTING_FAMILY_RUNTIME_MODULE,

  sheet: {
    typeLabels: ["Office Desk & Chairs"],
    applyCanonicalLabelsExport: "isDiningTableChairPair",
    isSheetOrSioTypeExport: "isDiningTableChairPair",
  },
  waClassify: {
    displayNameBySubcategoryId: {
      "office-table-chairs": "Office Desk & Chairs",
    },
    inferAttrsFromTitle: true,
  },
  triage: {
    pluralSlugMap: {
      "office-table-chairs": OFFICE_TABLE_CHAIRS_SUBCATEGORY_ID,
    },
    titleRulesIntent: ["office table chairs → office-table-chairs"],
  },
  jiji: {
    slugBySubcategoryId: {
      "office-table-chairs": "furniture",
    },
  },

  hidesSizeLine: true,
  hidesBrandLine: false,
  hidesModelLine: false,
  usesStructuredTitle: true,
  includeInReferencePricingCompsNest: true,
};

const FURNITURE_SURFACE_MEMBER_IDS = [
  "center-table",
  "office-table",
  "tv-console",
  "wardrobe",
  "dresser",
] as const satisfies readonly Exclude<
  (typeof FURNITURE_SURFACE_SUBCATEGORY_IDS)[number],
  "table"
>[];

const FURNITURE_SURFACE_FAMILY: ListingFamilyRegistration = {
  familyId: "furniture_surface",
  pluginKind: "furniture_surface",
  formStateKey: "furnitureSurfaceSpec",
  sectionLabel: "Table details",

  canonicalSubcategoryId: "table",
  parentCategoryId: "furniture",
  legacyAliasIds: [],
  memberSubcategoryIds: [...FURNITURE_SURFACE_MEMBER_IDS],
  // Hide combined legacy picker id only — do NOT put it in legacyAliasIds
  // (must not canonical-collapse wardrobe-dresser via registry).
  pickerHideIds: [LEGACY_WARDROBE_DRESSER_SUBCATEGORY_ID],
  deprecatedReplacements: {},

  attrKeys: [
    "table_top_material",
    "furniture_material",
    "length_ft",
    "width_ft",
    "height_ft",
  ],
  attrDefs: [
    {
      key: "table_top_material",
      label: "Table top material",
      required: false,
      enumValues: DINING_TABLE_TOP_MATERIALS,
    },
    {
      key: "furniture_material",
      label: "Material",
      required: false,
      enumValues: FURNITURE_MATERIALS,
    },
    {
      key: "length_ft",
      label: "Length (ft)",
      required: false,
    },
    {
      key: "width_ft",
      label: "Width (ft)",
      required: false,
    },
    {
      key: "height_ft",
      label: "Height (ft)",
      required: false,
    },
  ],

  listingSpecModule: "@/lib/listing/furnitureSurfaceListingSpec",
  specFieldsComponent: "@/components/listing/FurnitureSurfaceSpecFields",
  pricingCohortModule: "@/lib/pricing/furnitureSellSocketPricingCohort",
  runtimeModule: LISTING_FAMILY_RUNTIME_MODULE,

  sheet: {
    typeLabels: [
      "Tables",
      "Center Tables",
      "Office Tables",
      "TV Consoles",
      "Wardrobes",
      "Dressers",
      "Wardrobe / Dresser",
    ],
    applyCanonicalLabelsExport: "applyWardrobeDresserTitleSplit",
    isSheetOrSioTypeExport: "isWardrobeDresserBucketType",
  },
  waClassify: {
    displayNameBySubcategoryId: {
      table: "Tables",
      "center-table": "Center Tables",
      "office-table": "Office Tables",
      "tv-console": "TV Consoles",
      wardrobe: "Wardrobes",
      dresser: "Dressers",
    },
    inferAttrsFromTitle: true,
  },
  triage: {
    pluralSlugMap: {
      tables: "table",
      "center-tables": "center-table",
      "office-tables": "office-table",
      "tv-consoles": "tv-console",
      wardrobes: "wardrobe",
      dressers: "dresser",
    },
    titleRulesIntent: [
      "table / center / office / tv console / wardrobe / dresser → surface peers",
    ],
  },
  jiji: {
    slugBySubcategoryId: {
      table: "furniture",
      "center-table": "furniture",
      "office-table": "furniture",
      "tv-console": "furniture",
      wardrobe: "furniture",
      dresser: "furniture",
    },
  },

  hidesSizeLine: true,
  hidesBrandLine: false,
  hidesModelLine: false,
  usesStructuredTitle: true,
  includeInReferencePricingCompsNest: true,
};

/** Catalog of registered families (pilots + D6 + furniture sell-socket migrate). */
export const LISTING_FAMILY_CATALOG: readonly ListingFamilyRegistration[] = [
  SEWING_MACHINE_FAMILY,
  PLAY_STATION_FAMILY,
  FAN_FAMILY,
  FRIDGE_FAMILY,
  GAS_COOKER_FAMILY,
  MICROWAVE_FAMILY,
  WASHING_MACHINE_FAMILY,
  AC_FAMILY,
  KITCHEN_CABINET_FAMILY,
  CHAIR_FAMILY,
  SOFA_FAMILY,
  DINING_TABLE_CHAIR_FAMILY,
  TABLE_CHAIR_FAMILY,
  OFFICE_TABLE_CHAIRS_FAMILY,
  FURNITURE_SURFACE_FAMILY,
];

/**
 * Plugin kinds owned by the registry for hub membership (Phase 4 new-family gate).
 * Do not add hard-coded `is*Subcategory` membership for these in plugin / picker / pricing widen.
 */
export const REGISTRY_OWNED_PLUGIN_KINDS = [
  "sewing_machine",
  "play_station",
  "fan",
  "fridge",
  "gas_cooker",
  "microwave",
  "washing_machine",
  "ac",
  "kitchen_cabinet",
  "chair",
  "sofa",
  "dining_table_chair",
  "table_chair",
  "office_table_chairs",
  "furniture_surface",
] as const;

/** True when `kind` is a registry-owned plugin kind (sell socket dispatch). */
export function isRegistryOwnedPluginKind(
  kind: string | null | undefined,
): kind is ListingFamilyPluginKind {
  return (
    kind != null &&
    (REGISTRY_OWNED_PLUGIN_KINDS as readonly string[]).includes(kind)
  );
}

/**
 * Pre-registry family kinds still resolved by hard-coded hub branches.
 * D6 + furniture sell-socket migrate complete — empty.
 */
export const HARD_CODED_LISTING_FAMILY_PLUGIN_KINDS = [] as const;

const byFamilyId = new Map<ListingFamilyId, ListingFamilyRegistration>(
  LISTING_FAMILY_CATALOG.map((row) => [row.familyId, row]),
);

const bySubcategoryId = new Map<string, ListingFamilyRegistration>();
for (const family of LISTING_FAMILY_CATALOG) {
  bySubcategoryId.set(family.canonicalSubcategoryId, family);
  for (const legacyId of family.legacyAliasIds) {
    bySubcategoryId.set(legacyId, family);
  }
  for (const memberId of family.memberSubcategoryIds ?? []) {
    bySubcategoryId.set(memberId, family);
  }
}

export function listListingFamilies(): readonly ListingFamilyRegistration[] {
  return LISTING_FAMILY_CATALOG;
}

export function getListingFamilyById(
  familyId: string | null | undefined,
): ListingFamilyRegistration | null {
  if (!familyId) return null;
  return byFamilyId.get(familyId as ListingFamilyId) ?? null;
}

export function resolveListingFamilyBySubcategoryId(
  subcategoryId: string | null | undefined,
): ListingFamilyRegistration | null {
  if (!subcategoryId) return null;
  return bySubcategoryId.get(subcategoryId) ?? null;
}

/** True when subcategory is canonical or a registered legacy alias for any family. */
export function isRegisteredListingFamilySubcategory(
  subcategoryId: string | null | undefined,
): boolean {
  return resolveListingFamilyBySubcategoryId(subcategoryId) != null;
}

/**
 * Resolve canonical subcategory id via registry.
 * - Canonical or member → identity (that id).
 * - Legacy alias → family's canonicalSubcategoryId.
 * - Unregistered → null.
 */
export function resolveCanonicalSubcategoryIdFromRegistry(
  subcategoryId: string | null | undefined,
): string | null {
  const family = resolveListingFamilyBySubcategoryId(subcategoryId);
  if (!family || !subcategoryId) return null;
  if (subcategoryId === family.canonicalSubcategoryId) {
    return subcategoryId;
  }
  if ((family.memberSubcategoryIds ?? []).includes(subcategoryId)) {
    return subcategoryId;
  }
  if (family.legacyAliasIds.includes(subcategoryId)) {
    return family.canonicalSubcategoryId;
  }
  return null;
}

/** Attr keys declared for the family that owns this subcategory (empty if unknown). */
export function getListingFamilyAttrKeys(
  subcategoryId: string | null | undefined,
): readonly string[] {
  return resolveListingFamilyBySubcategoryId(subcategoryId)?.attrKeys ?? [];
}

/** Picker-hide ids for one family (empty if unknown). */
export function getListingFamilyPickerHideIds(
  familyId: string | null | undefined,
): readonly string[] {
  return getListingFamilyById(familyId)?.pickerHideIds ?? [];
}

/** Aggregate hide set across all registered families (Phase 2 `activeCategories` input). */
export function getAllRegisteredPickerHideIds(): ReadonlySet<string> {
  const ids = new Set<string>();
  for (const family of LISTING_FAMILY_CATALOG) {
    for (const id of family.pickerHideIds) {
      ids.add(id);
    }
  }
  return ids;
}

/** True when subcategory is a picker-hidden legacy id in any registered family. */
export function isHiddenFromSellPickerByListingFamilyRegistry(
  subcategoryId: string | null | undefined,
): boolean {
  return (
    typeof subcategoryId === "string" &&
    getAllRegisteredPickerHideIds().has(subcategoryId)
  );
}

/** True when subcategory belongs to the given registered family id. */
export function isListingFamilyMember(
  subcategoryId: string | null | undefined,
  familyId: ListingFamilyId,
): boolean {
  return (
    resolveListingFamilyBySubcategoryId(subcategoryId)?.familyId === familyId
  );
}

/**
 * Plugin kind from registry, or null on miss (hub falls back to hard-coded).
 */
export function resolveListingFamilyPluginKind(
  subcategoryId: string | null | undefined,
): ListingFamilyPluginKind | null {
  return resolveListingFamilyBySubcategoryId(subcategoryId)?.pluginKind ?? null;
}

/** Section label for Step 2 when family is registered. */
export function getListingFamilySectionLabel(
  subcategoryId: string | null | undefined,
): string | null {
  return (
    resolveListingFamilyBySubcategoryId(subcategoryId)?.sectionLabel ?? null
  );
}

/** Whether the registered family hides the generic size line. */
export function listingFamilyHidesSizeLine(
  subcategoryId: string | null | undefined,
): boolean {
  return (
    resolveListingFamilyBySubcategoryId(subcategoryId)?.hidesSizeLine === true
  );
}

/** Whether the registered family hides the category brand picker. */
export function listingFamilyHidesBrandLine(
  subcategoryId: string | null | undefined,
): boolean {
  return (
    resolveListingFamilyBySubcategoryId(subcategoryId)?.hidesBrandLine === true
  );
}

/** Whether the registered family hides the generic model field. */
export function listingFamilyHidesModelLine(
  subcategoryId: string | null | undefined,
): boolean {
  return (
    resolveListingFamilyBySubcategoryId(subcategoryId)?.hidesModelLine === true
  );
}

/**
 * Pricing widen ids for a registered family: canonical + legacy + members.
 * Null when subcategory is not in the catalog.
 */
export function getListingFamilyPricingWidenIds(
  subcategoryId: string | null | undefined,
): readonly string[] | null {
  const family = resolveListingFamilyBySubcategoryId(subcategoryId);
  if (!family) return null;
  return [
    family.canonicalSubcategoryId,
    ...family.legacyAliasIds,
    ...(family.memberSubcategoryIds ?? []),
  ];
}

/** Merge registered jiji slug maps (registry wins on key clash). */
export function getRegisteredJijiSlugBySubcategoryId(): Readonly<
  Record<string, string>
> {
  const out: Record<string, string> = {};
  for (const family of LISTING_FAMILY_CATALOG) {
    Object.assign(out, family.jiji.slugBySubcategoryId);
  }
  return out;
}

/** Merge registered triage plural → canonical subcategory maps. */
export function getRegisteredTriagePluralSlugMap(): Readonly<
  Record<string, { subcategory: string }>
> {
  const out: Record<string, { subcategory: string }> = {};
  for (const family of LISTING_FAMILY_CATALOG) {
    for (const [plural, canonical] of Object.entries(
      family.triage.pluralSlugMap,
    )) {
      out[plural] = { subcategory: canonical };
    }
  }
  return out;
}

/** Merge registered WA preferred sheet type labels by subcategory id. */
export function getRegisteredWaPreferredSheetTypes(): Readonly<
  Record<string, string>
> {
  const out: Record<string, string> = {};
  for (const family of LISTING_FAMILY_CATALOG) {
    Object.assign(out, family.waClassify.displayNameBySubcategoryId);
  }
  return out;
}

/** True when a complete registered family requires reference comps nest. */
export function registeredFamilyRequiresReferencePricingCompsNest(
  subcategoryId: string | null | undefined,
): boolean {
  return (
    resolveListingFamilyBySubcategoryId(subcategoryId)
      ?.includeInReferencePricingCompsNest === true
  );
}

/**
 * Contract completeness check for tests / Phase 4 gate.
 * Returns missing field paths; empty array means complete.
 */
export function getListingFamilyRegistrationGaps(
  family: ListingFamilyRegistration,
): string[] {
  const gaps: string[] = [];
  const requireNonEmpty = (path: string, value: unknown) => {
    if (value == null) {
      gaps.push(path);
      return;
    }
    if (typeof value === "string" && value.trim() === "") {
      gaps.push(path);
      return;
    }
    if (Array.isArray(value) && value.length === 0) {
      gaps.push(path);
    }
  };

  requireNonEmpty("familyId", family.familyId);
  requireNonEmpty("pluginKind", family.pluginKind);
  requireNonEmpty("formStateKey", family.formStateKey);
  requireNonEmpty("sectionLabel", family.sectionLabel);
  requireNonEmpty("canonicalSubcategoryId", family.canonicalSubcategoryId);
  requireNonEmpty("parentCategoryId", family.parentCategoryId);
  // legacyAliasIds + pickerHideIds may be empty (e.g. chair / dining / members-only).
  requireNonEmpty("attrKeys", family.attrKeys);
  requireNonEmpty("attrDefs", family.attrDefs);
  requireNonEmpty("listingSpecModule", family.listingSpecModule);
  requireNonEmpty("specFieldsComponent", family.specFieldsComponent);
  requireNonEmpty("pricingCohortModule", family.pricingCohortModule);
  requireNonEmpty("runtimeModule", family.runtimeModule);
  requireNonEmpty("sheet.typeLabels", family.sheet.typeLabels);
  requireNonEmpty(
    "sheet.applyCanonicalLabelsExport",
    family.sheet.applyCanonicalLabelsExport,
  );
  requireNonEmpty(
    "sheet.isSheetOrSioTypeExport",
    family.sheet.isSheetOrSioTypeExport,
  );
  requireNonEmpty(
    "waClassify.displayNameBySubcategoryId",
    Object.keys(family.waClassify.displayNameBySubcategoryId),
  );
  requireNonEmpty(
    "triage.pluralSlugMap",
    Object.keys(family.triage.pluralSlugMap),
  );
  requireNonEmpty(
    "jiji.slugBySubcategoryId",
    Object.keys(family.jiji.slugBySubcategoryId),
  );

  if (!family.includeInReferencePricingCompsNest) {
    gaps.push("includeInReferencePricingCompsNest");
  }

  const coverIds = [
    family.canonicalSubcategoryId,
    ...family.legacyAliasIds,
    ...(family.memberSubcategoryIds ?? []),
  ];
  for (const id of coverIds) {
    if (!(id in family.jiji.slugBySubcategoryId)) {
      gaps.push(`jiji.slugBySubcategoryId.${id}`);
    }
    if (!(id in family.waClassify.displayNameBySubcategoryId)) {
      gaps.push(`waClassify.displayNameBySubcategoryId.${id}`);
    }
  }

  for (const legacyId of family.legacyAliasIds) {
    if (!(legacyId in family.deprecatedReplacements)) {
      gaps.push(`deprecatedReplacements.${legacyId}`);
    }
    if (!family.pickerHideIds.includes(legacyId)) {
      gaps.push(`pickerHideIds missing ${legacyId}`);
    }
  }

  for (const key of family.attrKeys) {
    if (!family.attrDefs.some((def) => def.key === key)) {
      gaps.push(`attrDefs missing ${key}`);
    }
  }

  return gaps;
}

export function isCompleteListingFamilyRegistration(
  family: ListingFamilyRegistration,
): boolean {
  return getListingFamilyRegistrationGaps(family).length === 0;
}
